import { LitElement, TemplateResult, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { SlotHelper } from '../../controllers/slot-helper';

import innerStyles from './outline-container.inner.pcss?inline';
import globalStyles from './outline-container.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

export type ContentWidths =
  | 'unrestricted'
  | 'full'
  | 'wide'
  | 'medium'
  | 'narrow';

/**
 * These are intended to be a direct passthrough of
 * the outline.theme.pcss spacing globals, and should not
 * need to be hooked up or altered on this component, rather
 * set in outline.theme.pcss
 */
export type Space =
  | 'space-default'
  | 'space-0'
  | 'space-3xs'
  | 'space-2xs'
  | 'space-xs'
  | 'space-s'
  | 'space-m'
  | 'space-l'
  | 'space-xl'
  | 'space-2xl'
  | 'space-3xl'
  | 'space-4xl';

export type JustifyContent =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly';

/**
 * Aside from transparent, these are just placeholders.
 * Update with your own brand/design system options.
 * Strive to match the Figma vars/tokens naming precisely
 * to maximize maintainability.
 */
export type BackgroundColor =
  | 'transparent'
  | 'neutral-white'
  | 'brand-primary'
  | 'brand-secondary'
  | 'brand-tertiary';

/**
 * The Outline Container component
 * @element outline-container
 *
 * @slot - default
 * @slot background-graphics - Slot for background graphics that will be absolutely positioned
 *
 * @attr top-padding
 * @attr bottom-padding
 * @attr x-padding
 * @attr x-padding-mobile-none
 * @attr right-padding-none
 * @attr right-padding-mobile-none
 * @attr gap
 * @attr content-max-width
 * @attr justify-content
 * @attr background-color
 *
 * Creates a container component and accompanying set of utility
 * classes for import into other components. (Preferred over
 * placing components within other component's shadow roots.)
 *
 * The container component defines two areas, the container and
 * container wrapper. The container wrapper goes to the full bounds
 * of the host element. The container is the part which gets constrained
 * via padding, max-width, etc. This allows the creation of a full bleed
 * colored section with content of limited width.
 *
 * Note that many of the CSS vars used in this component's styles are
 * directly used globals, and are not further processed/derived.
 */
@customElement('outline-container')
export class OutlineContainer extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  slots = new SlotHelper(this);

  /**
   * The vertical space from the component above it, using
   * space variables defined in outline.theme.pcss.
   * Note that this sets the padding inside, so background
   * color will fill this space created.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'top-padding',
  })
  topPadding: Space = 'space-0';

  /**
   * Same as top-padding but for the bottom. Background color
   * will fill this space created.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'bottom-padding',
  })
  bottomPadding: Space = 'space-0';

  /**
   * The horizontal (left/right) padding of the component.
   * Background color will fill this space as well. Note that
   * this works with the content-max-width value to ensure that,
   * at maximum width, content with different padding values will
   * all align vertically assuming the same content-max-width value.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'x-padding',
  })
  xPadding: Space = 'space-default';

  /**
   * Remove left/right padding on mobile to go full width.
   */
  @property({
    type: Boolean,
    attribute: 'x-padding-mobile-none',
  })
  xPaddingMobileNone = false;

  /**
   * Remove the right padding of the component for content to go to screen edge.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'right-padding-none',
  })
  rightPaddingNone = false;

  /**
   * Remove the right padding of the component for content to go to screen edge on .
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'right-padding-mobile-none',
  })
  rightPaddingMobileNone = false;

  /**
   * Container content is displayed vertically using flex-column.
   * This gap represents the space between individual items placed
   * in the container.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'gap',
  })
  gap: Space = 'space-0';

  /**
   * Sets the width limit for the container's content.
   *
   * In CSS this property interacts with the xPadding attribute
   * to ensure that elements align when they hit their limits.
   * max-width: calc(
   *   var(--container--content-max-width) + 2 * var(--container--x-padding)
   * );
   *
   * The 'full' value is meant to represent your design's maximum
   * content width, whereas 'unrestricted' is intended to remove the
   * max-width limitation entirely.
   *
   * Background color goes into the container wrapper, so it has no
   * width restriction.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'content-max-width',
  })
  contentMaxWidth: ContentWidths = 'unrestricted';

  /**
   * Container content is displayed vertically using flex-column.
   * Justify content does exactly what it says.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'justify-content',
  })
  justifyContent: JustifyContent = 'flex-start';

  /**
   * Sets the background color for the container wrapper.
   * The critical distinction here is that this goes all the way
   * to the edge of the parent element, and is not constrained by
   * content-max-width and will fill the space created with padding.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: BackgroundColor = 'transparent';

  render(): TemplateResult {
    const hasBackgroundGraphics = this.slots.isNotEmpty('background-graphics');

    const containerWrapperClasses = {
      'container-wrapper': true,
      [`container-wrapper--background-color--${this.backgroundColor}`]: true,
      'container-wrapper--has-background-graphics': hasBackgroundGraphics,
    };

    const containerClasses = {
      container: true,
      [`container--top-padding--${this.topPadding}`]: true,
      [`container--bottom-padding--${this.bottomPadding}`]: true,
      [`container--x-padding--${this.xPadding}`]: true,
      [`container--x-padding--mobile-none`]: this.xPaddingMobileNone,
      [`container--right-padding--none`]: this.rightPaddingNone,
      [`container--right-padding--mobile-none`]: this.rightPaddingMobileNone,
      [`container--gap--${this.gap}`]: true,
      [`container--content-max-width--${this.contentMaxWidth}`]: true,
      [`container--justify-content--${this.justifyContent}`]: true,
    };

    return html`
      <div class="${classMap(containerWrapperClasses)}">
        ${hasBackgroundGraphics
          ? html`<slot name="background-graphics"></slot>`
          : nothing}
        <div class="${classMap(containerClasses)}">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-container': OutlineContainer;
  }
}
